import { z } from "zod";

import { entityIdSchema, isoInstantSchema, localeSchema } from "./primitives";

/**
 * Themes (SPEC §32–§37).
 *
 * Theme *source code* lives in the application repository. The content
 * repository stores only `{ "theme": { "id", "version" } }`, and D1 stores the
 * registry plus the per-site selection. A theme never touches D1, GitHub or the
 * CMS API — it receives normalized content and nothing else (SPEC §32).
 */

/** Loose semver: `1.0.0`, `2.1.3-beta.1`. */
export const themeVersionSchema = z
  .string()
  .regex(/^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/, "Must be a semantic version such as 1.0.0");

/**
 * Declared capabilities of a theme (SPEC §34).
 *
 * The build reads these to decide whether to emit a search index, render
 * comments or generate a feed, so they must be explicit rather than inferred.
 */
export const themeFeaturesSchema = z
  .object({
    darkMode: z.boolean(),
    search: z.boolean(),
    comments: z.boolean(),
    toc: z.boolean(),
    rss: z.boolean(),
  })
  .strict();

export type ThemeFeatures = z.infer<typeof themeFeaturesSchema>;

export const themeManifestSchema = z
  .object({
    id: z.string().min(1).max(100),
    version: themeVersionSchema,
    name: z.string().min(1).max(200),

    /** Locales this theme can actually render. */
    supportedLocales: z.array(localeSchema).min(1),

    features: themeFeaturesSchema,

    /** Version of the theme's own config schema, for migration decisions. */
    configSchemaVersion: z.string().min(1).max(50),
  })
  .strict();

export type ThemeManifest = z.infer<typeof themeManifestSchema>;

/**
 * A registered theme (SPEC §54 `themes`).
 *
 * Not site-scoped: themes are code shipped with the application, shared by every
 * site. The per-site choice lives in `site_theme_config`.
 */
export const themeSchema = z
  .object({
    id: z.string().min(1).max(100),
    version: themeVersionSchema,
    name: z.string().min(1).max(200),
    manifest: themeManifestSchema,

    /** True for themes shipped inside this repository. */
    isBuiltin: z.boolean(),

    createdAt: isoInstantSchema,
    updatedAt: isoInstantSchema,
  })
  .strict()
  .refine((theme) => theme.manifest.id === theme.id, {
    message: "manifest.id must match the registered theme id",
    path: ["manifest", "id"],
  })
  .refine((theme) => theme.manifest.version === theme.version, {
    message: "manifest.version must match the registered theme version",
    path: ["manifest", "version"],
  });

export type Theme = z.infer<typeof themeSchema>;

/**
 * Generic theme configuration payload (SPEC §37).
 *
 * Intentionally untyped: the *shape* belongs to the theme, which validates it
 * against its own schema. Invalid configuration must block deployment rather
 * than silently fall back (AGENTS.md §34).
 */
export const themeConfigSchema = z.record(z.unknown());

export type ThemeConfig = z.infer<typeof themeConfigSchema>;

/** The per-site theme selection and configuration (SPEC §54 `site_theme_config`). */
export const siteThemeConfigSchema = z
  .object({
    siteId: entityIdSchema,
    themeId: z.string().min(1).max(100),
    themeVersion: themeVersionSchema,
    config: themeConfigSchema,
    updatedAt: isoInstantSchema,
  })
  .strict();

export type SiteThemeConfig = z.infer<typeof siteThemeConfigSchema>;

/**
 * The `theme` block stored in the content repository's `config/site.json`
 * (SPEC §35).
 *
 * Only the selection is versioned alongside content; the config itself stays in
 * D1 so that changing it does not require a content commit.
 */
export const contentThemeRefSchema = z
  .object({
    id: z.string().min(1).max(100),
    version: themeVersionSchema,
  })
  .strict();

export type ContentThemeRef = z.infer<typeof contentThemeRefSchema>;
